import { paidPlatformName } from '../../../Marketing/shared';

export function fmtNumber(n: number): string {
  return new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 }).format(n);
}

export function fmtCurrency(n: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'EUR',
    maximumFractionDigits: 0,
  }).format(n);
}

export function fmtPercent(n: number, digits = 1): string {
  return `${n.toFixed(digits)}%`;
}

/** A signed number with a real minus (U+2212): "+3", "−2.5pp". Zero has no sign. The one place signs are written. */
export function fmtSigned(n: number, digits = 0, suffix = ''): string {
  const abs = Math.abs(n).toFixed(digits);
  const zero = Number(abs) === 0;
  return `${zero ? '' : n > 0 ? '+' : '−'}${digits === 0 ? fmtNumber(Number(abs)) : abs}${suffix}`;
}

export function fmtDelta(deltaPercent: number | null): { text: string; trend: 'up' | 'down' | 'neutral' } {
  if (deltaPercent === null || Number.isNaN(deltaPercent)) return { text: '—', trend: 'neutral' };
  const trend = deltaPercent > 0 ? 'up' : deltaPercent < 0 ? 'down' : 'neutral';
  // A real minus sign (U+2212), as on the visibility deltas, so a "-" never sits beside a "−".
  const sign = deltaPercent > 0 ? '+' : deltaPercent < 0 ? '−' : '';
  return { text: `${sign}${Math.abs(deltaPercent).toFixed(1)}%`, trend };
}

/**
 * Format a scorecard headline value. Counts and currency use compact notation
 * (e.g. 22.9K, €17K) so large figures fit inside the stat cards; percentages
 * keep their exact rate.
 */
export function fmtScorecard(value: number, unit: 'count' | 'percent' | 'currency'): string {
  if (unit === 'percent') return fmtPercent(value);
  if (unit === 'currency') {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'EUR',
      notation: 'compact',
      maximumFractionDigits: 1,
    }).format(value);
  }
  // count
  return new Intl.NumberFormat('en-US', {
    notation: 'compact',
    maximumFractionDigits: 1,
  }).format(value);
}

/** "vs prior 30d" — labels the comparison window so the delta is unambiguous. */
export function fmtComparisonLabel(periodDays: number): string {
  return `vs prior ${periodDays}d`;
}

/** "+48% vs pre-launch" line for the baseline comparison, or null when absent. */
export function fmtBaselineLabel(vsBaselinePercent: number | null | undefined): string | null {
  if (vsBaselinePercent === null || vsBaselinePercent === undefined || Number.isNaN(vsBaselinePercent)) {
    return null;
  }
  return `${fmtDelta(vsBaselinePercent).text} vs pre-launch`;
}

const REGION_NAMES = (() => {
  try {
    return new Intl.DisplayNames(['en'], { type: 'region' });
  } catch {
    return null;
  }
})();

/** Search Console reports ISO alpha-3 codes; the markets we see most, mapped to alpha-2 for Intl. */
const ALPHA3_TO_ALPHA2: Record<string, string> = {
  BEL: 'BE', NLD: 'NL', FRA: 'FR', DEU: 'DE', AUT: 'AT', CHE: 'CH', LUX: 'LU', GBR: 'GB', IRL: 'IE',
  USA: 'US', CAN: 'CA', AUS: 'AU', NZL: 'NZ', IND: 'IN', SGP: 'SG', ESP: 'ES', PRT: 'PT', ITA: 'IT',
  DNK: 'DK', SWE: 'SE', NOR: 'NO', FIN: 'FI', POL: 'PL', CZE: 'CZ', HUN: 'HU', ROU: 'RO', GRC: 'GR',
  TUR: 'TR', ISR: 'IL', ARE: 'AE', SAU: 'SA', ZAF: 'ZA', BRA: 'BR', MEX: 'MX', JPN: 'JP', CHN: 'CN',
  KOR: 'KR', PHL: 'PH', PAK: 'PK', NGA: 'NG', EGY: 'EG', UKR: 'UA', RUS: 'RU', BGR: 'BG', HRV: 'HR',
  SVK: 'SK', SVN: 'SI', EST: 'EE', LVA: 'LV', LTU: 'LT', MYS: 'MY', IDN: 'ID', VNM: 'VN', THA: 'TH',
};

/**
 * One display form for a country, whatever the source sent: ISO codes become
 * names ("FI" → "Finland", "bel" → "Belgium") and shouted names are cased
 * ("SINGAPORE" → "Singapore"). Unknown values are only re-cased.
 */
export function countryName(value: string | null | undefined): string {
  const raw = (value ?? '').trim();
  if (!raw || /^\(not set\)$/i.test(raw)) return 'Unknown';
  const upper = raw.toUpperCase();
  const alpha2 = /^[A-Z]{2}$/.test(upper) ? upper : /^[A-Z]{3}$/.test(upper) ? ALPHA3_TO_ALPHA2[upper] : undefined;
  if (alpha2 && REGION_NAMES) {
    const name = REGION_NAMES.of(alpha2);
    if (name && name !== alpha2) return name;
  }
  if (raw === upper || raw === raw.toLowerCase()) {
    return raw
      .toLowerCase()
      .replace(/(^|[\s(-])([a-z])/g, (_m, pre: string, c: string) => pre + c.toUpperCase())
      .replace(/\b(And|Of|The)\b/g, (w) => w.toLowerCase());
  }
  return raw;
}

/**
 * A column worth its own space: at least a quarter of the rows have a value.
 * Below that it is a column of dots with one or two figures in it (Paid spend's
 * "Platform conv." had 2 values, Core Web Vitals' "Overall" 1 of 24), so the
 * table hides it and the footer states the few values once.
 */
export function isSparseColumn<T>(rows: readonly T[], hasValue: (row: T) => boolean, minShare = 0.25): boolean {
  if (rows.length === 0) return true;
  return rows.filter(hasValue).length / rows.length < minShare;
}

/**
 * The ICP card's warning pill, naming the side that is mostly (over half)
 * unclassified, since one side can be fine while the other isn't.
 */
export function mostlyUnclassifiedLabel(pipelineShare: number | undefined, leadShare: number | undefined): string | null {
  const pipeline = (pipelineShare ?? 0) > 0.5;
  const leads = (leadShare ?? 0) > 0.5;
  if (pipeline && leads) return 'Leads and pipeline mostly unclassified';
  if (leads) return 'Leads mostly unclassified';
  if (pipeline) return 'Pipeline mostly unclassified';
  return null;
}

/** Ad platforms with a spend source connected, by their web-channel key. */
const SPEND_PLATFORM_BY_CHANNEL: Record<string, { platform: string; label: string }> = {
  paid_google: { platform: 'google', label: 'Google' },
  paid_meta: { platform: 'meta', label: 'Meta' },
  paid_linkedin: { platform: 'linkedin', label: 'LinkedIn' },
};

/** Paid channels with no spend source connected, named by platform. */
const UNCONNECTED_PLATFORM_LABELS: Record<string, string> = { paid_bing: 'Bing' };

export interface PaidClickSessionRow {
  label: string;
  /** Ad clicks the platform reports; null when no spend source is connected for it. */
  clicks: number | null;
  /** Sessions OS tracking attributes to that platform's paid traffic. */
  sessions: number;
  /** Spend comes from Odoo's monthly bills (Bing until Microsoft Ads syncs): no click counts. */
  spendFromBills?: boolean;
}

/**
 * Pure: per ad platform, the clicks it reports against the sessions OS
 * tracking saw from it ("Meta 326 → 11"). Paid sessions from a platform with no
 * spend row (Bing, OpenAI) are listed by platform with `clicks: null` so the page can say no spend
 * source is connected, rather than show 0 clicks. QA rows ("agenttest / cpc")
 * are dropped, as the Website tab hides them. Platforms with clicks and no
 * tracked session are kept with sessions 0. Sorted by clicks, unconnected
 * sources last.
 */
export function paidClickSessions(
  campaigns: ReadonlyArray<{ platform: string; clicks: number; spendSource?: string }>,
  channels: ReadonlyArray<{ channel: string; source: string; medium: string; sessions: number }>,
): PaidClickSessionRow[] {
  const clicksBy = new Map<string, number>();
  for (const c of campaigns) clicksBy.set(c.platform, (clicksBy.get(c.platform) ?? 0) + c.clicks);
  const sessionsBy = new Map<string, number>();
  const unconnected = new Map<string, number>();
  for (const r of channels) {
    if (!r.channel.startsWith('paid')) continue;
    if (/test/i.test(r.source) || /test/i.test(r.medium)) continue;
    const known = SPEND_PLATFORM_BY_CHANNEL[r.channel];
    if (known) {
      sessionsBy.set(known.platform, (sessionsBy.get(known.platform) ?? 0) + r.sessions);
      continue;
    }
    // By platform, not source / medium: "bing / cpc" and "bing / msclkid" are one Bing line.
    const label = UNCONNECTED_PLATFORM_LABELS[r.channel] ?? paidPlatformName(r.source);
    unconnected.set(label, (unconnected.get(label) ?? 0) + r.sessions);
  }
  const rows: PaidClickSessionRow[] = Object.values(SPEND_PLATFORM_BY_CHANNEL)
    .filter(({ platform }) => clicksBy.has(platform) || sessionsBy.has(platform))
    .map(({ platform, label }) => ({ label, clicks: clicksBy.get(platform) ?? null, sessions: sessionsBy.get(platform) ?? 0 }))
    .sort((a, b) => (b.clicks ?? 0) - (a.clicks ?? 0));
  const billed = campaigns.some((c) => c.platform === 'bing' && c.spendSource === 'odoo_bills');
  const rest = [...unconnected.entries()]
    .map(([label, sessions]) => ({ label, clicks: null, sessions, ...(billed && label === 'Bing' ? { spendFromBills: true } : {}) }))
    .sort((a, b) => b.sessions - a.sessions);
  return [...rows, ...rest];
}

/**
 * Pure: change between the average of the first `days` readings and the last
 * `days`, the way Search Visibility compares a period's ends, so one odd day
 * can't swing it. Null readings are skipped; null when there are fewer than
 * two readings. Short series use half their readings at each end.
 */
export function endsAverageChange(
  readings: ReadonlyArray<number | null>,
  days = 7,
): { first: number; last: number; change: number; days: number } | null {
  const r = readings.filter((v): v is number => v !== null && Number.isFinite(v));
  if (r.length < 2) return null;
  const n = Math.max(1, Math.min(days, Math.floor(r.length / 2)));
  const avg = (xs: number[]) => xs.reduce((s, v) => s + v, 0) / xs.length;
  const first = avg(r.slice(0, n));
  const last = avg(r.slice(-n));
  return { first, last, change: last - first, days: n };
}

/**
 * The markets we sell in. Each one is always listed under AI visibility by
 * market: on 13 Oct the UK, the biggest lead market, simply vanished from the
 * 90-day table because Peec had run no UK prompts since July.
 */
export const AI_TARGET_MARKETS = ['GB', 'NL', 'BE', 'DE', 'FR'] as const;

/** Pure: Peec's per-market rows plus any target market it has no row for (visibility null), best first. */
export function withTargetMarkets(
  rows: ReadonlyArray<{ countryCode: string; visibility: number | null }>,
): Array<{ countryCode: string; visibility: number | null }> {
  const seen = new Set(rows.map((r) => r.countryCode.toUpperCase()));
  const missing = AI_TARGET_MARKETS.filter((c) => !seen.has(c)).map((countryCode) => ({ countryCode, visibility: null }));
  return [...rows, ...missing].sort((x, y) => (y.visibility ?? -1) - (x.visibility ?? -1));
}
